import { createTheme } from '@material-ui/core/styles/';

declare module '@material-ui/core/styles/createBreakpoints' {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    medium: true;
    tablet: true;
    large: true;
    extraLarge: true;
  }
}

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    grey1: React.CSSProperties['color'];
    orange1: React.CSSProperties['color'];
    blue1: React.CSSProperties['color'];
    blue2: React.CSSProperties['color'];
    purple1: React.CSSProperties['color'];
    white: React.CSSProperties['color'];
    cyanRGB: React.CSSProperties['color'];
    redRGB: React.CSSProperties['color'];
    lightRed: React.CSSProperties['color'];
    black: React.CSSProperties['color'];
    customizeBlueRGB: React.CSSProperties['color'];
    pink: React.CSSProperties['color'];
    wheat: React.CSSProperties['color'];
    lightGrey: React.CSSProperties['color'];
    tomato: React.CSSProperties['color'];
    turquoise: React.CSSProperties['color'];
    darkGreyRGB: React.CSSProperties['color'];
    oceanBlue: React.CSSProperties['color'];
    lightOrange: React.CSSProperties['color'];
    whiteRGB: React.CSSProperties['color'];
    grey2: React.CSSProperties['color'];
    grey3: React.CSSProperties['color'];
    grey4: React.CSSProperties['color'];
    grey5: React.CSSProperties['color'];
    peach: React.CSSProperties['color'];
    green: React.CSSProperties['color'];
    green2: React.CSSProperties['color'];
    lightGreen: React.CSSProperties['color'];
    transparent: React.CSSProperties['color'];
    wppNavy: React.CSSProperties['color'];
    wppBlack: React.CSSProperties['color'];
    orange900: React.CSSProperties['color'];
    orange800: React.CSSProperties['color'];
    orange700: React.CSSProperties['color'];
    orange600: React.CSSProperties['color'];
    orange500: React.CSSProperties['color'];
  }
  interface PaletteOptions {
    grey1: React.CSSProperties['color'];
    orange1: React.CSSProperties['color'];
    blue1: React.CSSProperties['color'];
    blue2: React.CSSProperties['color'];
    purple1: React.CSSProperties['color'];
    white: React.CSSProperties['color'];
    cyanRGB: React.CSSProperties['color'];
    redRGB: React.CSSProperties['color'];
    lightRed: React.CSSProperties['color'];
    black: React.CSSProperties['color'];
    customizeBlueRGB: React.CSSProperties['color'];
    pink: React.CSSProperties['color'];
    wheat: React.CSSProperties['color'];
    lightGrey: React.CSSProperties['color'];
    tomato: React.CSSProperties['color'];
    turquoise: React.CSSProperties['color'];
    darkGreyRGB: React.CSSProperties['color'];
    oceanBlue: React.CSSProperties['color'];
    lightOrange: React.CSSProperties['color'];
    whiteRGB: React.CSSProperties['color'];
    grey2: React.CSSProperties['color'];
    grey3: React.CSSProperties['color'];
    grey4: React.CSSProperties['color'];
    grey5: React.CSSProperties['color'];
    peach: React.CSSProperties['color'];
    green: React.CSSProperties['color'];
    green2: React.CSSProperties['color'];
    lightGreen: React.CSSProperties['color'];
    transparent: React.CSSProperties['color'];
    wppNavy: React.CSSProperties['color'];
    wppBlack: React.CSSProperties['color'];
    orange900: React.CSSProperties['color'];
    orange800: React.CSSProperties['color'];
    orange700: React.CSSProperties['color'];
    orange600: React.CSSProperties['color'];
    orange500: React.CSSProperties['color'];
  }
}

const fontFamilyName = "'WPP', Arial, sans-serif";

// WPP Enterprise Solutions brand tokens (mirrored as CSS variables in index.css)
export const wppColors = {
  navy: '#000050',
  black: '#191919',
  orange900: '#6a290a',
  orange800: '#d94e0e',
  orange700: '#ff7800',
  orange600: '#f9bd5d',
  orange500: '#fff5cd',
  white: '#ffffff',
};

export const theme = createTheme({
  breakpoints: {
    values: {
      medium: 768,
      tablet: 960,
      large: 1280,
      extraLarge: 1440,
    },
  },
  typography: {
    fontFamily: fontFamilyName,
  },
  palette: {
    grey1: '#828282',
    orange1: wppColors.orange700,
    blue1: wppColors.navy,
    blue2: wppColors.navy,
    purple1: '#8884d8',
    white: wppColors.white,
    cyanRGB: 'rgba(0, 209, 217, 0.94)',
    redRGB: 'rgba(242, 27, 27, 0.94)',
    lightRed: '#f19392',
    darkGreyRGB: 'rgba(33, 35, 34, 0.2)',
    black: wppColors.black,
    customizeBlueRGB: 'rgba(0, 0, 80, 0.6)',
    pink: '#E661B2',
    wheat: wppColors.orange500,
    lightGrey: '#D6D6D6',
    tomato: wppColors.orange800,
    turquoise: '#00D1D9',
    oceanBlue: wppColors.navy,
    lightOrange: wppColors.orange700,
    whiteRGB: 'rgba(255,255,255,0.2)',
    grey2: '#525252',
    grey3: '#E0E5ED',
    grey4: '#818C98',
    grey5: '#bec4cf',
    peach: 'rgba(255, 120, 0, 0.6)',
    green: '#8bc34a',
    green2: '#35753a',
    lightGreen: '#80da87',
    transparent: 'rgba(255, 255, 255, 0)',
    wppNavy: wppColors.navy,
    wppBlack: wppColors.black,
    orange900: wppColors.orange900,
    orange800: wppColors.orange800,
    orange700: wppColors.orange700,
    orange600: wppColors.orange600,
    orange500: wppColors.orange500,
  },
});

theme.overrides = {
  MuiCssBaseline: {
    '@global': {
      body: {
        backgroundColor: 'white',
      },
      input: {
        fontFamily: fontFamilyName,
      },
      button: {
        fontFamily: fontFamilyName,
      },
      ul: {
        padding: 0,
        margin: 0,
        border: 0,
      },
      div: {
        padding: 0,
        margin: 0,
        border: 0,
      },
      nav: {
        padding: 0,
        margin: 0,
        border: 0,
      },
      '.react-datepicker *' : {
        fontFamily: fontFamilyName,
      },
      '@media (prefers-reduced-motion: no-preference)': {
        '.App-logo': {
          animation: 'App-logo-spin infinite 20s linear',
        },
      },
      '.App-link': {
        color: '#61dafb',
      },
      '.app-layout': {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      },
      '.main': {
        display: 'flex',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      },
      '.content': {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        position: 'relative'
      },
      // Grows to fill the viewport so the footer stays out of sight until the
      // user scrolls past the page content.
      '.content-body': {
        flex: '1 0 auto',
      },

      '.page-content': {
        margin: '20px',

        [theme.breakpoints.up('large')]: {
          margin: '25px 30px',
        },
      },

      '.page-title': {
        fontWeight: 400,
        color: theme.palette.black,
        fontSize: '24px',
        lineHeight: '100%',
        margin: '0',

        [theme.breakpoints.up('large')]: {
          fontSize: '30px',
        },
      },

      '#root': {
        position: 'relative',
        height: '100%',
        width: '100%',
      },
      '.App': {
        textAlign: 'center',
      },
      '.link-cta': {
        width: '80%',
        backgroundColor: '#900',
        padding: '14px',
        margin: '20px',
      },
      '@keyframes App-logo-spin': {
        from: {
          transform: 'rotate(0deg)',
        },
        to: {
          transform: 'rotate(360deg)',
        },
      },
    },
  },
};